import React from "react"

import { citationLabel } from "./copilotCitations"
import { CopilotCitation } from "./copilotStudioClient"

import styles from "./CopilotStudioSdkChat.module.sass"

export type CitationPanelSource = {
  position: number
  citation: CopilotCitation
  /** Verified SharePoint document, or why it is not. */
  verified: boolean
  reason: string
  claims: string[]
  loading: boolean
}

function claimText(claim: string) {
  return claim
    .replace(/\[\d+\]/g, "")
    .replace(/\*\*|__|[*`]/g, "")
    .replace(/^\s*[-*]\s+/, "")
    .replace(/\s*\|\s*/g, " · ")
    .replace(/^·\s*|\s*·$/g, "")
    .replace(/\s+/g, " ")
    .trim()
}

/**
 * LAB-113: what a citation marker stands for - the file, the passage that supports the
 * claim, its page, and a link to the original document.
 */
const CopilotCitationPanel: React.FC<{
  source: CitationPanelSource
  onClose: () => void
}> = ({ source, onClose }) => {
  const closeRef = React.useRef<HTMLButtonElement>(null)
  const { citation, position } = source
  const label = citationLabel(citation)

  React.useEffect(() => {
    closeRef.current?.focus()
  }, [position])

  React.useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose()
    }
    document.addEventListener("keydown", onKey)
    return () => document.removeEventListener("keydown", onKey)
  }, [onClose])

  let passage: React.ReactNode
  if (citation.excerpt) {
    passage = (
      <blockquote className={styles.citationExcerpt}>
        {citation.excerpt}
      </blockquote>
    )
  } else if (source.loading) {
    passage = (
      <p className={styles.citationMuted}>
        Finding the passage in this document…
      </p>
    )
  } else if (!source.verified) {
    passage = (
      <p className={styles.citationMuted}>
        Passages are shown for SharePoint documents only.
      </p>
    )
  } else {
    passage = (
      <p className={styles.citationMuted}>
        The passage could not be located in the document text. Open the document
        to check it.
      </p>
    )
  }

  return (
    <aside
      className={styles.citationPanel}
      role="dialog"
      aria-labelledby="copilot-citation-title"
    >
      <div className={styles.citationPanelHeader}>
        <span id="copilot-citation-title">Source {position}</span>
        <button
          ref={closeRef}
          type="button"
          className={styles.citationClose}
          onClick={onClose}
          aria-label="Close source"
        >
          ×
        </button>
      </div>
      <div className={styles.citationFile}>
        {citation.url ? (
          <a href={citation.url} target="_blank" rel="noreferrer">
            {label}
          </a>
        ) : (
          <span>{label}</span>
        )}
        <span
          className={
            source.verified ? styles.refStatusOk : styles.refStatusWarn
          }
        >
          {source.reason}
        </span>
      </div>
      <div className={styles.citationMeta}>
        {citation.page
          ? `Page ${citation.page}`
          : "Page or location not available"}
      </div>
      <div className={styles.citationSection}>Supporting passage</div>
      {passage}
      {source.claims.length ? (
        <>
          <div className={styles.citationSection}>Supports</div>
          <ul className={styles.citationClaims}>
            {source.claims.map((claim) => (
              <li key={claim}>{claimText(claim)}</li>
            ))}
          </ul>
        </>
      ) : null}
      {citation.url ? (
        <a
          className={styles.citationOpen}
          href={citation.url}
          target="_blank"
          rel="noreferrer"
        >
          Open document
        </a>
      ) : null}
    </aside>
  )
}

export default CopilotCitationPanel
